import { ItemCacheProvider, useItemCache } from '../context/ItemCacheContext';
import { IngredientCacheProvider, useIngredientCache } from '../context/IngredientCacheContext';
import NestedDocumentTable from '../components/NestedDocumentTable';
import WorkOrderHeader from '../components/WorkOrderHeader';
import WorkOrderStepper, { WorkOrderStatus } from '../components/WorkOrderStepper';
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useDocumentsManufacturing } from "../hooks/useDocumentsManufacturing";
import DocumentMasterHeader from "../components/DocumentMasterHeader";
import ShortcutNavBar from "../components/ShortcutNavBar";
import moment from "moment";
import { DocumentMaterial, DocumentType } from "../types/Types";
import { parseDecimalInput } from "../utils/decimalInput";
import ModalConfirmation from "../modals/ModalConfirmation";
import LoadingSpinner from "../assets/LoadingSpinner";
import ErrorComponent from "../assets/ErrorComponent";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { useMaterialDocumentPartners } from "../hooks/useMaterialDocumentPartners";
import { isRabotenNalog } from "../documents/docTypeChecks";

const DocumentsManufacturingContent: React.FC = () => {
    const nestedInputRefs = React.useRef<(HTMLInputElement | HTMLSelectElement | null)[][]>([]);
    const {
        document,
        clearDocument,
        loadingDocument,
        docs,
        loadingList,
        errorList,
        page,
        totalPages,
        total,
        absoluteTotal,
        setPage,
        search,
        setSearch,
        code,
        name,
        category,
        _id: documentTypeId,
        createDocument,
        loadDocumentById,
        selectedStatus,
        setSelectedStatus,
        statusFields,
        updateDocument,
        deleteDocument,
        documentTypeWarehouse,
        createWorkOrderLineItem
    } = useDocumentsManufacturing();

    const { documentTypes } = useDocumentTypesContext();
    const { fetchIngredientByItemId, fetchIngredientByIdWithMaterials } = useIngredientCache();
    const { fetchItemById } = useItemCache();

    const navigate = useNavigate();

    // Work Order specific fields
    const [selectedItemId, setSelectedItemId] = useState<string>(""); // This stores the ingredient ID
    const [actualItemId, setActualItemId] = useState<string>(""); // This stores the actual item (fixed asset) ID extracted from ingredient

    // Sub-recipe selection state (Option C: user picks ONE sub-recipe from ingredient's array)
    const [selectedSubRecipeIndex, setSelectedSubRecipeIndex] = useState<number | null>(null);
    const [ingredientFormulationBatch, setIngredientFormulationBatch] = useState<Array<{text?: string; quantity: number | null; mainUnit: any}>>([]);
    const [lockedIngredientId, setLockedIngredientId] = useState<string>("");
    const [ingredientVersion, setIngredientVersion] = useState<number | null>(null);

    // Planned values (from ingredients — read-only reference, no longer editable)
    const [batch, setBatch] = useState<number>(0);
    const [formulationBatch, setFormulationBatch] = useState<number>(0);
    const [waste, setWaste] = useState<number>(0);
    const [plannedWaste, setWasteKg] = useState<number>(0);
    const [batchSize, setBatchSize] = useState<number>(0); // Calculated: formulationBatch / batch

    // Actual values (manual entry)
    const [actualBatch, setActualBatch] = useState<number>(0);
    const [actualBatchKg, setActualBatchKg] = useState<number>(0);
    const [actualWaste, setActualWaste] = useState<number>(0);
    const [actualWasteKg, setActualWasteKg] = useState<number>(0);
    const [actualBatchSize, setActualBatchSize] = useState<number>(0); // Calculated: actualBatchKg / actualBatch

    // Implementation values (manual entry)
    const [implementationBatch, setImplementationBatch] = useState<number>(0);
    const [implementationBatchKg, setImplementationBatchKg] = useState<number>(0);
    const [implementationWaste, setImplementationWaste] = useState<number>(0);
    const [implementationWasteKg, setImplementationWasteKg] = useState<number>(0);
    const [implementationBatchSize, setImplementationBatchSize] = useState<number>(0); // Calculated: implementationBatchKg / implementationBatch
    const [serial, setSerial] = useState<string>("");
    const [deadline, setDeadline] = useState<string>("");
    const [itemDeadlineMonths, setItemDeadlineMonths] = useState<number | null>(null); // Store item's deadline months


    // Work order status machine state
    const [workOrderStatus, setWorkOrderStatus] = useState<WorkOrderStatus>(null);

    // MaterialIngredients state for auto-population
    const [materialIngredientsToPopulate, setMaterialIngredientsToPopulate] = useState<any[]>([]);


    // Common fields (personInCharge removed per NATASHA.md requirements)
    const [newNumber, setNewNumber] = useState("");
    const [selectedDate, setSelectedDate] = useState<string>(moment().format("YYYY-MM-DD"));

    const [isCreating, setIsCreating] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Validation state
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Reset trigger for nested table
    const [resetTrigger, setResetTrigger] = useState(0);

    // Delete confirmation modal state
    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

    // Track the last synced document ID to prevent duplicate syncs
    const [lastSyncedDocumentId, setLastSyncedDocumentId] = useState<string | null>(null);

    const issuanceMaterialDocs = useMemo(() => {
        if (!code || documentTypes.length === 0) return [];

        const docType = documentTypes.find((dt) => dt.code === code);
        if (!docType?.issuanceCreations) {
            return [];
        }

        const docs = docType.issuanceCreations
            .map((creation) => {
                const materialDoc =
                    typeof creation.materialDocument === "object"
                        ? creation.materialDocument
                        : documentTypes.find((dt) => dt._id === creation.materialDocument);

                return materialDoc && materialDoc.category === "material" ? materialDoc : null;
            })
            .filter((doc): doc is DocumentType => Boolean(doc));

        return Array.from(new Map(docs.map((doc) => [doc._id, doc])).values());
    }, [code, documentTypes]);

    const issuancePartnerSuggestions = useMaterialDocumentPartners(issuanceMaterialDocs);

    const issuancePartnerIds = useMemo(() => {
        if (issuanceMaterialDocs.length === 0) return [];

        const ids = issuanceMaterialDocs
            .map((doc) => {
                const warehouse = doc.warehouse;
                if (warehouse) {
                    return typeof warehouse === "object" ? warehouse._id : warehouse;
                }
                const suggestion = issuancePartnerSuggestions[doc._id];
                return suggestion?.id || null;
            })
            .filter((id): id is string => Boolean(id));

        return Array.from(new Set(ids));
    }, [issuanceMaterialDocs, issuancePartnerSuggestions]);

    const defaultPartnerId = useMemo(() => {
        if (issuancePartnerIds.length > 0) {
            return issuancePartnerIds[0];
        }
        return documentTypeWarehouse || null;
    }, [issuancePartnerIds, documentTypeWarehouse]);

    const defaultPartner = useMemo(() => {
        if (!defaultPartnerId) {
            return null;
        }
        const suggestionEntry = Object.values(issuancePartnerSuggestions).find(
            (info) => info.id === defaultPartnerId
        );
        const name = suggestionEntry?.name || "";
        return { _id: defaultPartnerId, name };
    }, [defaultPartnerId, issuancePartnerSuggestions]);

    // Batch size calculations are now done directly in WorkOrderHeader component
    // Green box (Planned): actualBatch / batch (Ingredients)
    // Orange box (Realized): implementationBatch / batch (Ingredients)

    // Recalculate deadline when document date changes
    useEffect(() => {
        if (itemDeadlineMonths && selectedDate) {
            const calculatedDeadline = moment(selectedDate).add(itemDeadlineMonths, 'months');
            setDeadline(calculatedDeadline.format('YYYY-MM-DD'));
        }
    }, [selectedDate, itemDeadlineMonths]);

    // Clear validation errors when required fields are filled
    useEffect(() => {
        if (showValidationErrors) {
            if (selectedItemId && actualItemId && selectedSubRecipeIndex !== null && selectedDate && selectedStatus) {
                setShowValidationErrors(false);
            }
        }
    }, [selectedItemId, actualItemId, selectedSubRecipeIndex, selectedDate, selectedStatus, showValidationErrors]);

    // Helper: sync item - now needs to find ingredient by item ID
    const syncItem = async (doc: DocumentMaterial) => {
        // @ts-ignore - item field in backend schema
        const itemId = doc.item?._id || doc.item || "";
        if (itemId) {
            setActualItemId(itemId);
            // Fetch ingredient that has this item (via cache)
            try {
                const result = await fetchIngredientByItemId(itemId);
                if (result?.ingredient) {
                    setSelectedItemId(result.ingredient._id);
                }
            } catch {
                setSelectedItemId("");
            }
        } else {
            setSelectedItemId("");
            setActualItemId("");
        }
    };

    // Helper: sync quantities and waste fields
    const syncQuantityAndBatch = (doc: DocumentMaterial) => {
        // Planned values (from ingredients)
        // @ts-ignore - batch, formulationBatch, waste, plannedWaste, and batchSize fields in backend schema
        setBatch(doc.batch || 0);
        // @ts-ignore
        setFormulationBatch(doc.formulationBatch || 0);
        // @ts-ignore
        setWaste(doc.waste || 0);
        // @ts-ignore
        setWasteKg(doc.plannedWaste || 0);
        // @ts-ignore
        setBatchSize(doc.batchSize || 0);

        // Actual values (manual entry)
        // @ts-ignore - actualBatch, actualBatchKg, actualWaste, actualWasteKg, and actualBatchSize fields in backend schema
        setActualBatch(doc.actualBatch || 0);
        // @ts-ignore
        setActualBatchKg(doc.actualBatchKg || 0);
        // @ts-ignore
        setActualWaste(doc.actualWaste || 0);
        // @ts-ignore
        setActualWasteKg(doc.actualWasteKg || 0);
        // @ts-ignore
        setActualBatchSize(doc.actualBatchSize || 0);

        // Implementation values (manual entry)
        // @ts-ignore - implementationBatch, implementationBatchKg, implementationWaste, implementationWasteKg, implementationBatchSize, serial, deadline fields in backend schema
        setImplementationBatch(doc.implementationBatch || 0);
        // @ts-ignore
        setImplementationBatchKg(doc.implementationBatchKg || 0);
        // @ts-ignore
        setImplementationWaste(doc.implementationWaste || 0);
        // @ts-ignore
        setImplementationWasteKg(doc.implementationWasteKg || 0);
        // @ts-ignore
        setImplementationBatchSize(doc.implementationBatchSize || 0);
        // @ts-ignore
        setSerial(doc.serial || "");
        // @ts-ignore
        setDeadline(doc.deadline || "");

    };

    // Helper: sync date
    const syncDate = (doc: DocumentMaterial) => {
        const formattedDate = doc.date ? moment(doc.date).format("YYYY-MM-DD") : "";
        setSelectedDate(formattedDate);
    };

    // Helper: sync status
    const syncStatus = (doc: DocumentMaterial) => {
        if (doc.status && statusFields.length > 0) {
            const matchingStatus = statusFields.find((s) => s._id === doc.status._id) ?? null;
            setSelectedStatus(matchingStatus);
        }
    };

    // Sync loaded document to local form state
    const syncDocumentFields = React.useCallback(async (doc: DocumentMaterial) => {
        await syncItem(doc);
        syncQuantityAndBatch(doc);
        syncDate(doc);
        syncStatus(doc);
        // @ts-ignore - workOrderStatus is added by backend
        const wos = (doc.workOrderStatus as WorkOrderStatus) ?? null;
        setWorkOrderStatus(wos);
        // Hydrate lockedIngredient from saved document
        // @ts-ignore - lockedIngredient is added by backend
        const li = doc.lockedIngredient as { ingredientId: string; selectedSubRecipeIndex: number } | undefined;
        if (li) {
            setLockedIngredientId(li.ingredientId);
            setSelectedSubRecipeIndex(li.selectedSubRecipeIndex);
        }
        setIsCreating(false);
        setIsEditing(false);
        setLastSyncedDocumentId(doc._id);
    }, [statusFields]);

    const resetForm = () => {
        clearDocument();
        setLastSyncedDocumentId(null);
        setSelectedItemId("");
        setActualItemId("");
        // Reset sub-recipe state
        setSelectedSubRecipeIndex(null);
        setIngredientFormulationBatch([]);
        setLockedIngredientId("");
        setIngredientVersion(null);

        // Reset planned values
        setBatch(0);
        setFormulationBatch(0);
        setWaste(0);
        setWasteKg(0);
        setBatchSize(0);

        // Reset actual values
        setActualBatch(0);
        setActualBatchKg(0);
        setActualWaste(0);
        setActualWasteKg(0);
        setActualBatchSize(0);

        // Reset implementation values
        setImplementationBatch(0);
        setImplementationBatchKg(0);
        setImplementationWaste(0);
        setImplementationWasteKg(0);
        setImplementationBatchSize(0);
        setSerial("");
        setDeadline("");
        setItemDeadlineMonths(null);

        // Reset work order status machine state
        setWorkOrderStatus(null);

        setSelectedDate(moment().toISOString());
        setIsCreating(true);
        setIsEditing(false);
        setNewNumber("");
        if (statusFields.length > 0) {
            setSelectedStatus(statusFields[0]);
        } else {
            setSelectedStatus(null);
        }
        // Trigger reset of nested table
        setResetTrigger(prev => prev + 1);
    };

    const handleEdit = () => {
        setIsCreating(false);
        setIsEditing(true);
    };

    const handleDeleteRequest = async () => {
        if (!document || !document._id) { return; }
        setConfirmDeleteModal(true);
    };

    const handleDeleteCancel = () => {
        setConfirmDeleteModal(false);
    };

    const handleDeleteConfirm = async () => {
        if (!document || !document._id) {
            return;
        }
        await deleteDocument(document._id);
        setIsCreating(false);
        setIsEditing(false);
        setConfirmDeleteModal(false);
    };

    // Sync document to state only once when document changes and not synced yet
    useEffect(() => {
        if (document && document._id !== lastSyncedDocumentId) {
            syncDocumentFields(document).catch(() => {
                // Sync failure is non-critical
            });
        }
    }, [document, lastSyncedDocumentId, syncDocumentFields]);

    // Initialize with empty string when component loads and no document exists
    useEffect(() => {
        if (!document && code && newNumber === "") {
            setNewNumber("");
        }
    }, [document, code, newNumber]);

    // Set default status when statusFields are loaded and no document is selected
    useEffect(() => {
        if (!document && statusFields.length > 0 && !selectedStatus) {
            setSelectedStatus(statusFields[0]);
        }
    }, [document, statusFields, selectedStatus]);

    // Auto-fetch MaterialIngredients and auto-fill Quantity/Batch when Fixed Asset is selected (for work orders)
    useEffect(() => {
        const fetchMaterialIngredients = async () => {
            // Reset deadline when ingredient changes
            setDeadline("");
            setItemDeadlineMonths(null);

            // Reset sub-recipe state when ingredient changes or clears
            setSelectedSubRecipeIndex(null);
            setIngredientFormulationBatch([]);
            setLockedIngredientId("");
            setIngredientVersion(null);

            // Only clear if no item is selected (don't clear when isCreating becomes false)
            if (!selectedItemId) {
                setMaterialIngredientsToPopulate([]);
                return;
            }

            // Only fetch when in creating mode
            if (!isCreating) {
                return;
            }

            try {
                // Fetch ingredient by ID with material ingredients (via cache)
                const result = await fetchIngredientByIdWithMaterials(selectedItemId);

                if (!result) {
                    setMaterialIngredientsToPopulate([]);
                    return;
                }

                const ingredient = result.ingredient;

                // Extract and store the actual item (Fixed Asset) ID from ingredient
                const itemId = typeof ingredient.items === 'object' ? (ingredient.items as any)._id : ingredient.items;
                if (itemId) {
                    setActualItemId(itemId);

                    // Fetch item details to get deadlineMonths (via cache)
                    try {
                        const item = await fetchItemById(itemId);
                        if (item && item.managingDeadline && item.deadlineMonths) {
                            setItemDeadlineMonths(item.deadlineMonths);
                            if (selectedDate) {
                                const calculatedDeadline = moment(selectedDate).add(item.deadlineMonths, 'months');
                                setDeadline(calculatedDeadline.format('YYYY-MM-DD'));
                            }
                        } else {
                            setItemDeadlineMonths(null);
                        }
                    } catch {
                        // Non-critical: item deadline fetch failure silently ignored
                    }
                }

                // Store ingredient lock info for sub-recipe reference card
                setLockedIngredientId(ingredient._id);
                setIngredientVersion(ingredient.version ?? null);

                // Handle formulationBatch — now an array of sub-recipes
                if (ingredient.formulationBatch && Array.isArray(ingredient.formulationBatch)) {
                    setIngredientFormulationBatch(ingredient.formulationBatch);
                } else {
                    setIngredientFormulationBatch([]);
                }

                // Still populate scalar planned values for backward-compat display
                if (ingredient.batch !== undefined && ingredient.batch !== null) {
                    setBatch(ingredient.batch);
                }
                if (ingredient.waste !== undefined && ingredient.waste !== null) {
                    setWaste(ingredient.waste);
                }
                if (ingredient.plannedWaste !== undefined && ingredient.plannedWaste !== null) {
                    setWasteKg(ingredient.plannedWaste);
                }

                setMaterialIngredientsToPopulate(result.materialIngredients);
            } catch {
                setMaterialIngredientsToPopulate([]);
            }
        };

        fetchMaterialIngredients();
    }, [selectedItemId, isCreating]);

    // Auto-populate nested table when document is created and MaterialIngredients are ready
    useEffect(() => {
        const populate = async () => {
            if (document && document._id && materialIngredientsToPopulate.length > 0 && batch > 0) {
                await autoPopulateNestedTable(document._id);
            }
        };

        populate();
    }, [document?._id, materialIngredientsToPopulate, batch]);

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = e.target.value;
        const status = statusFields.find((s) => s._id === selectedId) ?? null;
        setSelectedStatus(status);
    };

    const handleSaveDocument = async (): Promise<boolean> => {
        // Validate work order required fields (personInCharge removed per NATASHA.md)
        // selectedSubRecipeIndex must be explicitly chosen (null = not picked yet)
        if (!selectedItemId || !actualItemId || selectedSubRecipeIndex === null || !selectedDate || !selectedStatus) {
            setShowValidationErrors(true);
            return false;
        }

        // Clear validation errors if form is valid
        setShowValidationErrors(false);

        const headerData = {
            item: actualItemId, // Use the actual item (Fixed Asset) ID extracted from ingredient
            // Actual values (manual entry)
            actualBatch: actualBatch,
            actualBatchKg: actualBatchKg,
            actualWaste: actualWaste,
            actualWasteKg: actualWasteKg,
            // Implementation values (manual entry)
            implementationBatch: implementationBatch,
            implementationBatchKg: implementationBatchKg,
            implementationWaste: implementationWaste,
            implementationWasteKg: implementationWasteKg,
            serial: serial,
            deadline: deadline,
            date: selectedDate,
            status: selectedStatus?._id,
            // GMP compliance: lock the selected sub-recipe at creation time
            lockedIngredient: {
                ingredientId: lockedIngredientId,
                selectedSubRecipeIndex: selectedSubRecipeIndex,
            },
        };

        try {
            setIsSaving(true);
            if (isEditing) {
                // Update existing document
                await updateDocument(headerData);
                setIsEditing(false);
            } else {
                // Create new document
                await createDocument(headerData);
                setIsCreating(false);
                // Note: Auto-population happens in useEffect that watches document changes
            }
            setIsSaving(false);
            return true;
        } catch (error) {
            setIsSaving(false);
            return false;
        }
    };

    // Auto-populate nested table entries from MaterialIngredients
    const autoPopulateNestedTable = async (documentId: string) => {
        if (!batch || batch <= 0) {
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        try {
            for (const matIng of materialIngredientsToPopulate) {
                // Calculate quantityOS (ingredient quantity × batch)
                const ingredientQty = parseDecimalInput(matIng.quantity) ?? 0;
                const calculatedQuantityOS = ingredientQty * batch;

                // Fetch full item details to get valuationPrice and purchasePrice
                let valuationPrice = 0;
                let purchasePrice = 0;

                if (matIng.item?._id) {
                    try {
                        const itemDetails = await fetchItemById(matIng.item._id);
                        if (itemDetails) {
                            valuationPrice = itemDetails.valuationPrice || itemDetails.purchasePrice || 0;
                            purchasePrice = itemDetails.purchasePrice || 0;
                        }
                    } catch {
                        // Non-critical: price defaults to 0
                    }
                }

                // Calculate priceValue
                const priceValue = (valuationPrice * ingredientQty * batch).toFixed(2);

                // Use defaultPartner first, then fall back to documentTypeWarehouse
                const partnerId = defaultPartner?._id || defaultPartnerId || documentTypeWarehouse || null;

                const nestedEntry = {
                    document: documentId,
                    item: matIng.item?._id,
                    purchasePrice: purchasePrice,
                    valuationPrice: valuationPrice,
                    quantity: matIng.quantity || 0,
                    quantityOS: calculatedQuantityOS,
                    quantityWorkOrder: 0,
                    serial: "",
                    deadline: "",
                    partner: partnerId,
                    priceValue: priceValue
                };

                await createWorkOrderLineItem(nestedEntry);
            }

            setMaterialIngredientsToPopulate([]);
            setResetTrigger(prev => prev + 1);
        } catch {
            // Auto-population failure is non-critical; user can add rows manually
        }
    };

    if (loadingDocument || isSaving) return <LoadingSpinner />;
    if (errorList) return <ErrorComponent message={errorList} />;

    return (
        <div className="min-h-screen bg-gray-100 flex overflow-hidden">
            <div
                className="flex-1 flex flex-col transition-all duration-300"
                style={{
                    paddingLeft: '0',
          backgroundColor: 'var(--color-background)', // match Sidebar w-80
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden'
                }}
            >
                <div className="w-full">
                    <Navbar />
                </div>
                <div className="flex-1 flex flex-col overflow-hidden py-1 px-4 mt-12">
                    <ShortcutNavBar />
                    <DocumentMasterHeader
                        isCreating={isCreating}
                        isSaving={isSaving}
                        onReset={resetForm}
                        onSave={handleSaveDocument}
                        onDelete={handleDeleteRequest}
                        hasDocument={!!(document?.number)}
                        search={search}
                        setSearch={setSearch}
                        docs={docs}
                        loading={loadingList}
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        absoluteTotal={absoluteTotal}
                        setPage={setPage}
                        documentCode={code}
                        documentName={name}
                        documentTypeId={documentTypeId}
                        currentDocumentNumber={document?.number}
                        document={document ?? undefined}
                        category={category}
                        subCategory={undefined}
                        subType={undefined}
                        onDocumentSelect={(doc: DocumentMaterial) => {
                            // Clear all form fields immediately to prevent old data from showing
                            setSelectedItemId("");
                            setActualItemId("");
                            setBatch(0);
                            setFormulationBatch(0);
                            setWaste(0);
                            setWasteKg(0);
                            setBatchSize(0);
                            setSelectedDate("");
                            setSelectedStatus(null);
                            // Reset synced document ID to allow new document sync
                            setLastSyncedDocumentId(null);
                            // Load the selected document
                            loadDocumentById(doc._id);
                            setIsCreating(false);
                            setIsEditing(false);
                        }}
                    />

                    {/* Work Order status stepper — only shown for work-order category docs */}
                    {isRabotenNalog({ category }) && (
                        <WorkOrderStepper
                            workOrderStatus={workOrderStatus}
                            documentId={document?._id || ""}
                            createdDocInfo={null}
                            onStatusUpdated={setWorkOrderStatus}
                        />
                    )}

                    <WorkOrderHeader
                        documentNumber={document?.number || newNumber}
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                        selectedStatus={selectedStatus}
                        statusFields={statusFields}
                        onStatusChange={handleStatusChange}
                        selectedItemId={selectedItemId}
                        onItemChange={setSelectedItemId}
                        isEditable={
                            workOrderStatus === null ||
                            workOrderStatus === "draft"
                        }
                        showValidationErrors={showValidationErrors}
                        subRecipes={ingredientFormulationBatch}
                        selectedSubRecipeIndex={selectedSubRecipeIndex}
                        onSubRecipeChange={setSelectedSubRecipeIndex}
                        ingredientVersion={ingredientVersion}
                        ingredientBatch={batch}
                        ingredientWaste={waste}
                        ingredientPlannedWaste={plannedWaste}
                    />

                    <NestedDocumentTable
                        documentId={document?._id || ''}
                        inputRefs={nestedInputRefs}
                        resetTrigger={resetTrigger}
                        documentTypePartnerStock={documentTypeWarehouse}
                        defaultPartner={defaultPartner}
                        batch={batch}
                        documentCategory={category as "material" | "non-material" | "planning" | "work-order" | undefined}
                        isLocked={
                            workOrderStatus === "completed" ||
                            workOrderStatus === "cancelled"
                        }
                    />
                </div>
            </div>
            <ModalConfirmation
                isOpen={confirmDeleteModal}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                description={document?.number || "this document"}
            />
        </div>
    );
};

const DocumentsManufacturing: React.FC = () => (
    <IngredientCacheProvider>
        <ItemCacheProvider>
            <DocumentsManufacturingContent />
        </ItemCacheProvider>
    </IngredientCacheProvider>
);

export default DocumentsManufacturing;
